import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { UseFormReturn } from "react-hook-form";
import { PatchPolicyFormValues } from "../../schema";

interface UserExperienceTabProps {
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function UserExperienceTab({ form }: UserExperienceTabProps) {
  const showNotifications = form.watch("show_notifications");

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-2">User Deferral Options</h3>
        <p className="text-sm text-muted-foreground mb-6">Allow users to postpone update installations</p>

        <FormField
          control={form.control}
          name="show_notifications"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Allow users to defer installation</FormLabel>
                <FormDescription>
                  Give users the option to postpone update installations
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {showNotifications && (
        <div className="space-y-6 animate-fade-in">
          <FormField
            control={form.control}
            name="defer_max_times"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Maximum deferrals allowed</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input 
                        type="number" 
                        min={0}
                        max={20}
                        className="w-20"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <span className="text-sm text-muted-foreground">times</span>
                  </div>
                </div>
                <FormDescription>
                  How many times users can defer before installation is forced
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defer_duration_minutes"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Prompt frequency</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input 
                        type="number" 
                        min={1}
                        max={168}
                        className="w-20"
                        placeholder="24"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value) * 60)}
                        value={Math.round((field.value || 60) / 60)}
                      />
                    </FormControl>
                    <span className="text-sm text-muted-foreground">hours</span>
                  </div>
                </div>
                <FormDescription>
                  How often to remind users about pending updates (1-168 hours)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-6">Support Resources</h3>
        
        <FormField
          control={form.control}
          name="support_article_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Support Article URL (Optional)</FormLabel>
              <FormControl>
                <Input {...field} placeholder="https://support.example.com/updates" />
              </FormControl>
              <FormDescription>
                Link to support documentation shown to users during updates
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
